import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { WorkOrderListItem } from "@autorepair/contracts";
import { dashboardApi, invoicesApi, workOrdersApi } from "../lib/api";
import { formatDate, formatInvoiceNumber, formatMoney, formatWorkOrderNumber } from "../lib/money";
import { Spinner } from "../components/ui-modern";
import { AvatarBadge, Eyebrow, ModernCard, ModernErrorBanner, ModernStatusPill, StatCard } from "../components/ui-modern";

/**
 * The modern theme's Dashboard — see docs/FRONTEND_BACKLOG.md and
 * `Bay2Bill Redesign.dc.html` lines ~117-193 (the "isHome" block).
 *
 * Now a faithful recreation of the mockup's layout (an earlier round only
 * restyled a data SUBSTITUTION — see docs/FRONTEND_BACKLOG.md for that
 * history): 4 stat cards, a 3-column job board (Waiting / In the bays /
 * Ready to invoice), and a "Needs money in" unpaid-invoice list.
 *
 * The job board and invoice list use the SAME `workOrdersApi.list()` /
 * `invoicesApi.list()` endpoints the Work Orders/Invoices list pages
 * already call — no new list endpoint was needed, only two new aggregate
 * fields on `GET /dashboard` itself (`unbilledTotal`, `unpaidOver30DaysCount`,
 * see `DashboardService.summary`) for the two stat-card subtext figures
 * that weren't computable from existing fields.
 *
 * One honest gap, not fabricated: the mockup's "In the bays" subtext ("2
 * due out today") has no backing data — `work_orders` has no due/promised
 * date column at all, only `openedAt`/`approvedAt`/`completedAt`/
 * `invoicedAt`. Rather than invent a due-date concept, that card simply
 * has no subtext.
 *
 * Board card `meta` line: the mockup shows a clock time or elapsed
 * duration for Waiting/In the bays and a dollar total for Ready to
 * invoice. The dollar total is a real field (`total`); elapsed time since
 * `openedAt` is used for the other two columns (also real, computed
 * client-side) rather than reproducing the mockup's exact "opened at
 * HH:MM" for Waiting alone, which would need no computation beyond what's
 * already used for "In the bays" anyway. `job.service` uses `customerConcern`
 * (the closest real field to the mockup's short job-type label) — falls
 * back to "Work order" when a shop hasn't recorded one.
 */

function elapsedSince(iso: string): string {
  const ms = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    const remMins = mins % 60;
    return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
  }
  return `${Math.floor(hours / 24)}d`;
}

function techInitials(names: string | null): string {
  const first = names?.split(",")[0]?.trim();
  if (!first) return "—";
  const parts = first.split(/\s+/).filter(Boolean);
  return parts.length >= 2 ? `${parts[0]![0]}${parts[1]![0]}`.toUpperCase() : first.slice(0, 2).toUpperCase();
}

const BOARD_COLUMNS = [
  { key: "waiting", title: "Waiting", statuses: ["estimate", "approved"], accent: "#8b97a8", metaKind: "elapsed" as const },
  { key: "in_bays", title: "In the bays", statuses: ["in_progress"], accent: "#c98a1a", metaKind: "elapsed" as const },
  { key: "ready", title: "Ready to invoice", statuses: ["complete"], accent: "#1a6b2c", metaKind: "total" as const },
];

function JobCard({ workOrder, metaKind, accent }: { workOrder: WorkOrderListItem; metaKind: "elapsed" | "total"; accent: string }) {
  return (
    <Link
      to={`/work-orders/${workOrder.workOrderNumber}`}
      className="flex items-center gap-2.5 rounded-[10px] border border-[#e2e7ee] bg-white py-2.5 pl-3 pr-3 no-underline transition-colors hover:border-[#0f6d86]"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-semibold text-[#101b2d]">{workOrder.customerConcern || "Work order"}</div>
        <div className="mt-0.5 truncate text-[12px] text-[#6b7a8f]">{workOrder.vehicleDescription ?? "—"}</div>
        <div className="mt-0.5 font-mono text-[11px] text-[#8b97a8]" style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}>
          {formatWorkOrderNumber(workOrder.workOrderNumber)}
        </div>
      </div>
      <div className="flex flex-none flex-col items-end gap-1.5">
        <span className="text-[12.5px] font-semibold tabular-nums text-[#071a33]">
          {metaKind === "total" ? formatMoney(workOrder.total) : elapsedSince(workOrder.openedAt)}
        </span>
        <AvatarBadge initials={techInitials(workOrder.technicianNames)} size={26} fontSize={11} />
      </div>
    </Link>
  );
}

export function DashboardPageModern() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: dashboardApi.summary,
    staleTime: 0,
    refetchOnMount: "always",
  });
  // Bounded (not the whole tenant's history) — this is a glance-view board,
  // not a full list page (that's what /work-orders is for); the column
  // HEADER counts below still come from `data.workOrderCountsByStatus`
  // (an exact aggregate), only the cards shown per column are capped.
  const boardQuery = useQuery({
    queryKey: ["dashboard", "board"],
    queryFn: () => workOrdersApi.list({ limit: 50 }),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const unpaidQuery = useQuery({
    queryKey: ["dashboard", "unpaid-invoices"],
    queryFn: () => invoicesApi.list({ status: "unpaid", limit: 5 }),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const today = new Intl.DateTimeFormat(undefined, { weekday: "long", day: "numeric", month: "long" }).format(new Date());
  const boardItems = boardQuery.data?.items ?? [];

  return (
    <div>
      <div className="mb-5">
        <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">{today}</Eyebrow>
        <h1 className="m-0 text-[30px] font-bold tracking-[-.01em] text-[#071a33]">Today&apos;s workshop</h1>
      </div>

      {isLoading && <Spinner label="Loading today's overview…" />}
      {error && <ModernErrorBanner message="Could not load the dashboard. Please try again." />}

      {data && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            <Link to="/work-orders?status=in_progress" className="block h-full no-underline">
              <StatCard
                label="In the bays"
                value={data.workOrderCountsByStatus.in_progress}
                className="h-full transition-colors hover:border-[#0f6d86]"
              />
            </Link>
            <Link to="/work-orders?status=complete" className="block h-full no-underline">
              <StatCard
                label="Ready to invoice"
                value={data.workOrderCountsByStatus.complete}
                valueClassName="!text-[#1e7d32]"
                subtext={`${formatMoney(data.unbilledTotal)} unbilled`}
                className="h-full transition-colors hover:border-[#0f6d86]"
              />
            </Link>
            <Link to="/invoices?status=unpaid" className="block h-full no-underline">
              <StatCard
                label="Unpaid invoices"
                value={data.openInvoiceCount}
                valueClassName="!text-[#9a6700]"
                subtext={data.unpaidOver30DaysCount > 0 ? `${data.unpaidOver30DaysCount} over 30 days` : undefined}
                className="h-full transition-colors hover:border-[#0f6d86]"
              />
            </Link>
            <StatCard
              label="Owed to the shop"
              value={formatMoney(data.totalBalanceDue)}
              dark
              subtext={
                <Link to="/invoices?status=unpaid" className="text-inherit underline underline-offset-2 hover:text-white">
                  Across {data.openInvoiceCount} open invoice{data.openInvoiceCount === 1 ? "" : "s"}
                </Link>
              }
              className="h-full"
            />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-3.5 lg:grid-cols-3">
            {BOARD_COLUMNS.map((col) => {
              const count = col.statuses.reduce((sum, s) => sum + (data.workOrderCountsByStatus[s as keyof typeof data.workOrderCountsByStatus] ?? 0), 0);
              const items = boardItems.filter((w) => col.statuses.includes(w.status)).slice(0, 5);
              return (
                <ModernCard key={col.key} className="min-w-0">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="m-0 text-[14px] font-bold text-[#071a33]">{col.title}</h2>
                    <span
                      className="rounded-[999px] px-2 py-0.5 text-[12px] font-bold tabular-nums"
                      style={{ background: `${col.accent}1a`, color: col.accent }}
                    >
                      {count}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {items.length === 0 && <p className="m-0 text-[13px] text-[#8b97a8]">Nothing here right now.</p>}
                    {items.map((w) => (
                      <JobCard key={w.id} workOrder={w} metaKind={col.metaKind} accent={col.accent} />
                    ))}
                  </div>
                </ModernCard>
              );
            })}
          </div>

          <ModernCard className="min-w-0">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="m-0 text-[14px] font-bold text-[#071a33]">Needs money in</h2>
              <Link to="/invoices" className="text-[12.5px] font-semibold text-[#0f6d86] no-underline hover:underline">
                All invoices →
              </Link>
            </div>
            <div className="flex flex-col gap-2">
              {(unpaidQuery.data?.items.length ?? 0) === 0 && <p className="m-0 text-[13px] text-[#8b97a8]">No unpaid invoices right now.</p>}
              {unpaidQuery.data?.items.map((inv) => (
                <Link
                  key={inv.id}
                  to={`/invoices/${inv.invoiceNumber}`}
                  className="flex items-center justify-between gap-3 rounded-[10px] border border-[#e2e7ee] px-3.5 py-3 no-underline transition-colors hover:border-[#0f6d86]"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[13.5px] font-semibold text-[#101b2d]">{inv.customerNameSnapshot}</div>
                    <div className="mt-0.5 truncate text-[12px] text-[#6b7a8f]">
                      {formatInvoiceNumber(inv.invoiceNumber)} · {inv.vehicleDescriptionSnapshot} · {formatDate(inv.issuedOn)}
                      {inv.customerConcernSnapshot ? ` · ${inv.customerConcernSnapshot}` : ""}
                    </div>
                  </div>
                  <div className="flex flex-none items-center gap-2.5">
                    <span className="text-[13.5px] font-bold tabular-nums text-[#071a33]">{formatMoney(inv.balanceDue)}</span>
                    <ModernStatusPill status={inv.status} />
                  </div>
                </Link>
              ))}
            </div>
          </ModernCard>
        </>
      )}
    </div>
  );
}
